import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from 'react-query';
import { AxiosInstance } from '../axios.config';
import { api } from '../constants';
import { IGetProductParams } from '../manageProduct';
import { IProduct } from '../manageProduct/types';
import {
  AdminReview,
  AdminReviewsResponse,
  IGetReviewsByProductResponse,
  IReportsResponse,
} from './types';
/* eslint-disable @typescript-eslint/no-unused-vars */
const getProductsWithReviews = async (
  params: IGetProductParams,
): Promise<{ results: IProduct[]; pageSize: number; count: number }> => {
  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([_, value]) => value != null && value !== ''),
  );
  const response = await AxiosInstance.get(`${api.reviews.customer_reviews}?&is_review=${true}`, {
    params: filteredParams,
  });

  return response.data;
};
/* eslint-disable @typescript-eslint/no-unused-vars */
const useGetProductsWithReviews = (
  params: IGetProductParams,
): UseQueryResult<{
  results: IProduct[];
  pageSize: number;
  count: number;
  page_count: number;
}> => {
  return useQuery({
    queryKey: ['Reviews', params],
    queryFn: () => getProductsWithReviews(params),
    keepPreviousData: true,
  });
};

const getReviewsByProduct = async (params: {
  id: string;
  page: number;
  ordering?: string;
}): Promise<IGetReviewsByProductResponse> => {
  const response = await AxiosInstance.get<IGetReviewsByProductResponse>(
    api.reviews.list_by_product.replace(':id', params.id),
    {
      params: {
        page: params.page,
        ordering: params.ordering, // ← pass it here
      },
    },
  );
  return response.data;
};

const useGetReviewsByProduct = (params: {
  id: string;
  page: number;
  ordering?: string;
}): UseQueryResult<IGetReviewsByProductResponse> => {
  return useQuery({
    queryKey: ['Reviews', params.id, params.page, params.ordering], // ← ADD ordering!
    queryFn: () => getReviewsByProduct(params),
    keepPreviousData: true,
  });
};

const deleteReview = async (id: string) => {
  return await AxiosInstance.delete(api.reviews.delete.replace(':id', id));
};

const useDeleteReview = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: 'delete_review',
    mutationFn: deleteReview,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Reviews'] });
    },
  });
};

const getReportsByReview = async (params: {
  id: string;
  page: number;
}): Promise<IReportsResponse> => {
  const response = await AxiosInstance.get<IReportsResponse>(
    api.reviews.reports_by_review.replace(':id', params.id),
    {
      params: { page: params.page },
    },
  );
  return response.data;
};

const useGetReportsByReview = (params: { id: string; page: number }) => {
  return useQuery<IReportsResponse>({
    queryKey: ['Reports', params.id, params.page],
    queryFn: () => getReportsByReview(params),
    keepPreviousData: true,
  });
};

const deleteReport = (id: string) => {
  return AxiosInstance.delete(api.reviews.delete_report.replace(':id', id));
};

const useDeleteReport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: 'delete_report',
    mutationFn: deleteReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Reports'] });
    },
  });
};

// admin review
const getAdminReviews = async (params: {
  search?: string;
  page?: number;
  page_size?: number;
  start_at?: string;
  end_at?: string;
}): Promise<AdminReviewsResponse> => {
  const response = await AxiosInstance.get<AdminReviewsResponse>(api.reviews.admin.list, {
    params,
  });
  return response.data;
};

export const useGetAdminReviews = (params: {
  search?: string;
  page?: number;
  page_size?: number;
  start_at?: string;
  end_at?: string;
}): UseQueryResult<AdminReviewsResponse, Error> => {
  return useQuery({
    queryKey: ['adminReviews', params],
    queryFn: () => getAdminReviews(params),
    refetchOnMount: 'always',
  });
};

const createAdminReview = async (formData: FormData): Promise<AdminReview> => {
  const response = await AxiosInstance.post<AdminReview>(api.reviews.admin.create, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const useCreateAdminReview = (): UseMutationResult<AdminReview, Error, FormData> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createAdminReview,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminReviews'] });
    },
  });
};

const getAdminReview = async (id: number): Promise<AdminReview> => {
  const response = await AxiosInstance.get<AdminReview>(api.reviews.admin.get(id));
  return response.data;
};

export const useGetAdminReview = (
  id: number,
  options: { enabled: boolean },
): UseQueryResult<AdminReview, Error> => {
  return useQuery({
    queryKey: ['adminReview', id],
    queryFn: () => getAdminReview(id),
    enabled: options.enabled,
  });
};

const patchAdminReview = async ({
  id,
  formData,
}: {
  id: number;
  formData: FormData;
}): Promise<AdminReview> => {
  const response = await AxiosInstance.patch<AdminReview>(api.reviews.admin.patch(id), formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return response.data;
};

export const usePatchAdminReview = (): UseMutationResult<
  AdminReview,
  Error,
  { id: number; formData: FormData }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchAdminReview,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['adminReviews'] });
      queryClient.invalidateQueries({ queryKey: ['adminReview', variables.id] });
    },
  });
};

const deleteAdminReview = async (id: number): Promise<void> => {
  await AxiosInstance.delete(api.reviews.admin.delete(id));
};

export const useDeleteAdminReview = (): UseMutationResult<void, Error, number> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAdminReview,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminReviews'] });
    },
  });
};

interface HideAdminReview {
  id: string | number; // Adjusted to match the actual response payload
}

interface HideReviewResponse {
  id: number;
  message?: string;
  is_hidden?: boolean;
}

const createhideReview = async (formData: HideAdminReview): Promise<HideAdminReview> => {
  const response = await AxiosInstance.post<HideReviewResponse>(
    api.reviews.hide_report(formData.id),
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
  // Map the response to AdminReview, providing defaults if needed
  return {
    id: response.data.id,
  };
};

export const useHideReview = (): UseMutationResult<HideAdminReview, Error, HideAdminReview> => {
  const queryClient = useQueryClient();
  return useMutation<HideAdminReview, Error, HideAdminReview>({
    mutationFn: createhideReview,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminReviews'] });
    },
  });
};

const createUnHideReview = async (formData: HideAdminReview): Promise<HideAdminReview> => {
  const response = await AxiosInstance.post<HideReviewResponse>(
    api.reviews.unhide_report(formData.id),
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
  // Map the response to AdminReview, providing defaults if needed
  return {
    id: response.data.id,
  };
};

export const useUnHideReview = (): UseMutationResult<HideAdminReview, Error, HideAdminReview> => {
  const queryClient = useQueryClient();
  return useMutation<HideAdminReview, Error, HideAdminReview>({
    mutationFn: createUnHideReview,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminReviews'] });
    },
  });
};

export {
  useGetProductsWithReviews,
  useGetReviewsByProduct,
  useDeleteReview,
  useGetReportsByReview,
  useDeleteReport,
};
